import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import SearchSelect from './SearchSelect';
import Pager from './Pager';
import { formatDay } from '../utils/dates';

export const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
export const METHOD_LABEL = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', cheque: 'Cheque', saldo: 'Saldo a favor' };
const METHODS = ['efectivo', 'transferencia', 'tarjeta', 'cheque'];
const ADD_ACCOUNT = '__add__';
const EPS = 0.005;
const PAGE_SIZE = 10; // same client-side pagination convention as IngresosPage.js/InvoicesPage.js/etc.

// Today on the Dominican calendar (the server's day), whatever timezone this browser is in.
const todayDR = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' });
const dayDR = (d) => new Date(d).toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' });
const cents = (n) => Math.round((Number(n) || 0) * 100) / 100;
const dueDay = (d) => formatDay(d);

// Puts `total` on the invoices in order (each up to what can still be collected on it): the oldest / most overdue first.
const distribute = (total, list) => {
  const out = {};
  let left = cents(total);
  for (const inv of list) {
    const put = Math.min(left, inv.available);
    if (put > EPS) { out[inv._id] = String(cents(put)); left = cents(left - put); }
  }
  return out;
};

// "Nuevo ingreso": money that came in from a client. It can pay one invoice or several at once, and whatever isn't applied to
// an invoice stays as the client's saldo a favor (an anticipo). A client that already has saldo a favor can use it to pay
// invoices, with or without new money. Opened from "Cobrar" on an invoice (client + invoice preset), from the top button, or from
// a client's anticipo.
//   preset: { client?: id, invoice?: id, useCredit?: true }
const IngresoModal = ({ invoices, credits, preset = {}, onClose, onSaved }) => {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  const presetInvoice = preset.invoice ? invoices.find(i => i._id === preset.invoice) : null;
  const startClient = preset.client || presetInvoice?.client?._id || '';

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(startClient);
  const [cash, setCash] = useState(presetInvoice ? String(presetInvoice.available) : '');
  const [useCredit, setUseCredit] = useState('');
  const [alloc, setAlloc] = useState({});
  const [auto, setAuto] = useState(true); // keep spreading the money over the invoices until the person edits an amount
  const [method, setMethod] = useState('efectivo');
  const [account, setAccount] = useState('');
  const [date, setDate] = useState(todayDR());
  const [notes, setNotes] = useState('');
  const [accounts, setAccounts] = useState(null);
  const [newAccount, setNewAccount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Narrows which of the client's invoices are workable (and paginates them) — a big
  // client can have dozens of open invoices, which used to render as one long list.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    API.get('/clients').then(r => setClients(r.data)).catch(() => {});
    API.get('/bank-accounts').then(r => setAccounts(r.data)).catch(() => setAccounts([]));
  }, []);

  const client = clients.find(c => c._id === clientId);
  const clientName = client?.name || presetInvoice?.client?.name || credits.find(c => c.client._id === clientId)?.client.name || '';
  const creditAvailable = credits.find(c => c.client._id === clientId)?.credit || 0;

  // This client's invoices that can still take money: the one that was clicked first, then the most overdue.
  const open = useMemo(() => invoices
    .filter(i => i.client?._id === clientId && i.available > EPS)
    .sort((a, b) => (a._id === presetInvoice?._id ? -1 : b._id === presetInvoice?._id ? 1 : 0)), [invoices, clientId, presetInvoice]);

  // `open` narrowed to the selected date range (if any) — this, not `open`, is what
  // "Distribuir automáticamente", the table and Guardar actually work with, so setting a
  // range (e.g. "solo el mes pasado") really does limit which invoices get paid.
  const visibleOpen = useMemo(() => {
    if (!dateFrom && !dateTo) return open;
    return open.filter(i => { const d = dayDR(i.date); return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo); });
  }, [open, dateFrom, dateTo]);
  const visibleAvailable = cents(visibleOpen.reduce((s, i) => s + i.available, 0));

  const pages = Math.max(1, Math.ceil(visibleOpen.length / PAGE_SIZE));
  const pagedOpen = visibleOpen.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [clientId, dateFrom, dateTo]);

  // Coming from a client's anticipo: start by proposing to use it on their invoices.
  useEffect(() => {
    if (preset.useCredit && creditAvailable > 0 && useCredit === '') setUseCredit(String(cents(Math.min(creditAvailable, visibleAvailable))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset.useCredit, creditAvailable, visibleAvailable]);

  const cashN = cents(cash);
  const creditN = cents(useCredit);
  useEffect(() => {
    if (auto) setAlloc(distribute(cashN + Math.min(creditN, creditAvailable), visibleOpen));
  }, [auto, cashN, creditN, creditAvailable, visibleOpen]);

  // Only what's applied to a currently-visible invoice counts — an allocation left over in
  // state for an invoice the date filter just hid is not going to be submitted either (see
  // `submit` below), so it shouldn't count toward "aplicado" or the balance math here.
  const applied = cents(visibleOpen.reduce((s, i) => s + cents(alloc[i._id] || 0), 0));
  const creditUsed = cents(Math.min(creditN, applied));
  const fromCash = cents(applied - creditUsed);
  const advance = cents(cashN - fromCash);

  const problems = [];
  for (const inv of visibleOpen) {
    if (cents(alloc[inv._id]) > inv.available + EPS) problems.push(`La factura ${inv.number} solo admite ${money(inv.available)}.`);
  }
  if (creditUsed > creditAvailable + EPS) problems.push(`El cliente solo tiene ${money(creditAvailable)} de saldo a favor.`);
  if (fromCash > cashN + EPS) problems.push(`Aplicaste ${money(applied)} a facturas, pero solo hay ${money(cashN)} recibidos${creditUsed > 0 ? ` y ${money(creditUsed)} de saldo a favor` : ''}. Baja lo aplicado o sube el monto.`);
  const minDate = useMemo(() => {
    const dates = visibleOpen.filter(i => cents(alloc[i._id]) > 0).map(i => dayDR(i.date)).sort();
    return dates.length ? dates[dates.length - 1] : '';
  }, [visibleOpen, alloc]);
  const dateOk = date && date <= todayDR() && (!minDate || date >= minDate);
  const needsAccount = cashN > 0 && method === 'transferencia';
  const accountOk = !needsAccount || (account && account !== ADD_ACCOUNT);
  const nothing = cashN <= 0 && applied <= 0;
  const canSave = !!clientId && !nothing && problems.length === 0 && accountOk && dateOk && !busy;

  const editAlloc = (id, value) => { setAuto(false); setAlloc(prev => ({ ...prev, [id]: value })); };
  const handleAccountChange = (e) => {
    if (e.target.value === ADD_ACCOUNT) { setNewAccount(''); setAccount(''); } else { setNewAccount(null); setAccount(e.target.value); }
  };
  const saveNewAccount = async () => {
    setError('');
    try {
      const { data } = await API.post('/bank-accounts', { name: newAccount });
      setAccounts(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setAccount(data._id); setNewAccount(null);
    } catch (err) { setError(err.response?.data?.message || 'No se pudo agregar la cuenta.'); }
  };
  const changeClient = (c) => { setClientId(c._id); setAlloc({}); setAuto(true); setUseCredit(''); setCash(''); setDateFrom(''); setDateTo(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true); setError('');
    try {
      const { data } = await API.post('/payments/receipt', {
        client: clientId,
        allocations: visibleOpen.filter(i => cents(alloc[i._id]) > 0).map(i => ({ invoice: i._id, amount: cents(alloc[i._id]) })),
        cash: cashN,
        method: cashN > 0 ? method : undefined,
        account: needsAccount ? account : undefined,
        useCredit: creditUsed,
        date, notes: notes.trim() || undefined,
      });
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo registrar el ingreso.');
      setBusy(false);
    }
  };

  const summaryLine = (
    <div className="p-2 mb-3" style={{ background: 'var(--mm-panel-2)', borderRadius: 8 }}>
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <span>Recibido: <strong>{money(cashN)}</strong></span>
        {creditUsed > 0 && <span>Saldo a favor usado: <strong>{money(creditUsed)}</strong></span>}
        <span>Aplicado a facturas: <strong>{money(applied)}</strong></span>
        <span style={{ color: advance > EPS ? 'var(--mm-cyan)' : undefined }}>Queda como anticipo: <strong>{money(Math.max(advance, 0))}</strong></span>
      </div>
      {advance > EPS && (
        <div className="small text-muted mt-1">
          {money(advance)} quedan como <strong>saldo a favor</strong> de {clientName || 'el cliente'}, para pagar facturas futuras.
        </div>
      )}
    </div>
  );

  return (
    <Modal show onHide={busy ? undefined : onClose} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title>Nuevo ingreso</Modal.Title></Modal.Header>
        <Modal.Body>
          {!isAdmin && (
            <div className="alert alert-info small py-2">
              Este ingreso quedará <strong>por verificar</strong> hasta que un administrador lo apruebe: no cuenta como recibido ni baja el saldo de las facturas hasta entonces. Se les avisa por correo y no tienes que esperar.
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Cliente</Form.Label>
            {startClient ? (
              <div style={{ fontWeight: 600 }}>{clientName || 'Cliente'}{creditAvailable > 0 && <span className="ms-2 small" style={{ color: 'var(--mm-cyan)', fontWeight: 400 }}>saldo a favor {money(creditAvailable)}</span>}</div>
            ) : (
              <>
                <SearchSelect options={clients} value={clientId} getKey={c => c._id} getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
                  onSelect={changeClient} placeholder="Buscar cliente..." emptyLabel="Sin clientes coincidentes" />
                {clientId && creditAvailable > 0 && <div className="small mt-1" style={{ color: 'var(--mm-cyan)' }}>{clientName} tiene {money(creditAvailable)} de saldo a favor.</div>}
              </>
            )}
          </Form.Group>

          {clientId && (
            <>
              {open.length > 0 && (
                <div className="mb-3 p-2 d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ background: 'var(--mm-panel-2)', borderRadius: 8 }}>
                  <span className="text-muted small">
                    Total pendiente de {clientName || 'este cliente'} ({visibleOpen.length} factura{visibleOpen.length === 1 ? '' : 's'}
                    {(dateFrom || dateTo) ? ', en el rango seleccionado' : ', todo su historial'})
                  </span>
                  <strong style={{ fontSize: '1.1rem', color: 'var(--mm-yellow)' }}>{money(visibleAvailable)}</strong>
                </div>
              )}
              <Row>
                <Col md={creditAvailable > 0 ? 4 : 6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Monto recibido</Form.Label>
                    <Form.Control type="number" min="0" step="0.01" value={cash} onChange={e => setCash(e.target.value)} placeholder="0.00" autoFocus={!presetInvoice} />
                  </Form.Group>
                </Col>
                {creditAvailable > 0 && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Usar saldo a favor <span className="text-muted">(máx. {money(creditAvailable)})</span></Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control type="number" min="0" step="0.01" value={useCredit} onChange={e => setUseCredit(e.target.value)} placeholder="0.00" />
                        <Button type="button" variant="outline-secondary" className="flex-shrink-0" onClick={() => setUseCredit(String(cents(Math.min(creditAvailable, visibleAvailable))))}>Todo</Button>
                      </div>
                    </Form.Group>
                  </Col>
                )}
                <Col md={creditAvailable > 0 ? 4 : 6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fecha del cobro</Form.Label>
                    <Form.Control type="date" value={date} min={minDate || undefined} max={todayDR()} onChange={e => setDate(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>

              {cashN > 0 && (
                <Row>
                  <Col md={needsAccount ? 4 : 12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Forma de pago</Form.Label>
                      <Form.Select className="w-100" value={method} onChange={e => setMethod(e.target.value)}>
                        {METHODS.map(k => <option key={k} value={k}>{METHOD_LABEL[k]}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {needsAccount && (
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cuenta donde se recibió</Form.Label>
                        {accounts === null ? <div className="small text-muted">Cargando cuentas...</div> : (
                          <Form.Select className="w-100" value={newAccount !== null ? ADD_ACCOUNT : account} onChange={handleAccountChange}>
                            <option value="" disabled>Selecciona una cuenta...</option>
                            {accounts.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                            {isAdmin && <option value={ADD_ACCOUNT}>+ Agregar cuenta...</option>}
                          </Form.Select>
                        )}
                        {accounts && accounts.length === 0 && newAccount === null && (
                          <div className="small text-muted mt-1">{isAdmin ? 'Aún no hay cuentas bancarias: agrégala con la última opción de la lista.' : 'No hay cuentas bancarias registradas. Pídele a un administrador que las agregue.'}</div>
                        )}
                        {newAccount !== null && (
                          <div className="d-flex gap-2 mt-2">
                            <Form.Control autoFocus maxLength={80} placeholder="Ej.: Banco Popular · Corriente 1234" value={newAccount} onChange={e => setNewAccount(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newAccount.trim()) saveNewAccount(); } }} />
                            <Button type="button" variant="primary" className="flex-shrink-0" disabled={!newAccount.trim()} onClick={saveNewAccount}>Guardar</Button>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              )}

              {open.length > 0 && (
                <Row className="align-items-end mb-2 g-2">
                  <Col xs={6} md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Filtrar por fecha, desde</Form.Label>
                      <Form.Control size="sm" type="date" value={dateFrom} max={dateTo || undefined} onChange={e => setDateFrom(e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">hasta</Form.Label>
                      <Form.Control size="sm" type="date" value={dateTo} min={dateFrom || undefined} onChange={e => setDateTo(e.target.value)} />
                    </Form.Group>
                  </Col>
                  {(dateFrom || dateTo) && (
                    <Col xs="auto">
                      <Button type="button" size="sm" variant="outline-secondary" onClick={() => { setDateFrom(''); setDateTo(''); }}>Quitar filtro</Button>
                    </Col>
                  )}
                </Row>
              )}

              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="mb-0">
                  Facturas a pagar{' '}
                  <span className="text-muted">
                    ({visibleOpen.length}{visibleOpen.length !== open.length ? ` de ${open.length}` : ''} con saldo)
                  </span>
                </Form.Label>
                <div className="d-flex gap-2">
                  <Button type="button" size="sm" variant="outline-secondary" onClick={() => setAuto(true)} title="Reparte el dinero empezando por la factura más atrasada">Distribuir automáticamente</Button>
                  <Button type="button" size="sm" variant="outline-secondary" onClick={() => { setAuto(false); setAlloc({}); }}>Limpiar</Button>
                </div>
              </div>
              {open.length === 0 ? (
                <div className="small text-muted mb-3">{clientName || 'Este cliente'} no tiene facturas con saldo por cobrar. Lo que recibas quedará como anticipo (saldo a favor).</div>
              ) : visibleOpen.length === 0 ? (
                <div className="small text-muted mb-3">Ninguna factura de {clientName || 'este cliente'} cae en ese rango de fechas.</div>
              ) : (
                <>
                  <div className="table-responsive mb-2">
                    <Table size="sm" className="align-middle mb-0">
                      <thead><tr><th>Factura</th><th>Vence</th><th className="text-end">Se puede cobrar</th><th style={{ width: 150 }} className="text-end">Aplicar</th></tr></thead>
                      <tbody>
                        {pagedOpen.map(i => (
                          <tr key={i._id}>
                            <td><strong>{i.number}</strong>{i.pendingAmount > 0 && <div className="small text-muted">{money(i.pendingAmount)} por verificar</div>}</td>
                            <td className="small">{dueDay(i.dueDate)}{i.daysLate > 0 && <span className="ms-1" style={{ color: 'var(--mm-red)' }}>({i.daysLate} d)</span>}</td>
                            <td className="text-end">{money(i.available)}</td>
                            <td>
                              <Form.Control size="sm" type="number" min="0" step="0.01" className="text-end" value={alloc[i._id] ?? ''} placeholder="0.00"
                                isInvalid={cents(alloc[i._id]) > i.available + EPS} onChange={e => editAlloc(i._id, e.target.value)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {pages > 1 && <div className="d-flex justify-content-center mb-3"><Pager page={page} pages={pages} onChange={setPage} /></div>}
                </>
              )}

              {summaryLine}
              {problems.map(p => <div key={p} className="small mb-1" style={{ color: 'var(--mm-red)' }}>{p}</div>)}

              <Form.Group className="mt-3 mb-1">
                <Form.Label>Nota <span className="text-muted">(opcional)</span></Form.Label>
                <Form.Control maxLength={200} placeholder="Ej.: referencia de la transferencia, cheque #0042" value={notes} onChange={e => setNotes(e.target.value)} />
              </Form.Group>
            </>
          )}

          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" disabled={busy} onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!canSave}>
            {busy ? 'Registrando...'
              : isAdmin ? (cashN > 0 ? `Registrar ingreso de ${money(cashN)}` : 'Aplicar saldo a favor')
              : (cashN > 0 ? `Enviar ingreso de ${money(cashN)} a verificación` : 'Enviar a verificación')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default IngresoModal;
